import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { Form, Spinner, Badge, Accordion } from 'react-bootstrap';
import NumberInput from '../Generic/NumberInput';
import OrderProductLabel from './OrderProductLabel';
import './OrderComponentStyle.css';
import OrderItemLotComponent from './OrderItemLotComponent';
import OrderItemKitComponentLotComponent from './OrderItemKitComponentLotComponent';
import { Plus } from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import QuantityErrorMessage from './QuantityErrorMessage';
import { AppContext } from '../../App';
import { getKitStatus } from '../../clients/product_client';
import { isKitProduct } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { hasItemQuantityMismatch } from '../../utils/orderQuantityValidation';

/**
 * OrderItemComponent
 *
 * A component that builds a single order item within the order form, complete with necessary fields
 * and validation. Handles lot/batch management, quantity tracking, and status updates for order fulfillment.
 *
 * @param {Object} product - Contains the details of a product.
 * @param {Object} item - Represents a single order item.
 * @param {Array} orderObjItemLotItems - Array of stock lots associated with this order item.
 * @param {number} orderObjItemQuantity - The total quantity ordered for this item.
 * @param {string} orderItemStatus - Current status of the order item (e.g., 'fulfilled', 'partial', 'unfulfilled').
 * @param {function} onItemChange - Callback to execute when an item property changes. Updates the data of a specific order item.
 * @param {number} orderItemIndex - The index position of this item in the order items array.
 * @param {Object} quoteItem - Information about the quote for this order item.
 * @param {function} addLotItem - Callback to add a new lot/batch entry to this order item.
 * @param {function} handleLotItemDelete - Callback to remove a lot/batch entry from this order item.
 * @param {Object} formik - The Formik object containing form control methods, validation, and values.
 *
 * @returns {JSX.Element} A form section for managing a single order item with lot tracking.
 */
const OrderItemComponent = ({
  product,
  item,
  orderObjItemLotItems,
  orderObjItemQuantity,
  orderItemStatus,
  onItemChange,
  orderItemIndex,
  quoteItem,
  addLotItem,
  handleLotItemDelete,
  formik,
  allReceivedOK = false,
  storageNodes = null,
  storagePlaceOptions = null,
}) => {
  const { token } = useContext(AppContext);

  // State hook for managing delay in updating form field changes.
  const [typingTimeout, setTypingTimeout] = useState(null);

  // Kit component state
  const [kitComponents, setKitComponents] = useState([]);
  const [loadingKitComponents, setLoadingKitComponents] = useState(false);
  const hasProcessedExistingDataRef = useRef(false);

  // Helper to get quote item quantity
  const getQuoteItemQuantity = (quoteItem, item) => {
    return quoteItem ? quoteItem.quantity : item.quoteItem.quantity;
  };

  // Helper to create a default lot item
  const createDefaultLotItem = () => ({
    expiry: '',
    batch: '',
    lotItemQuantity: 0,
    stockItemIds: [],
  });

  // Global cache to track kit status attempts across all components
  const kitStatusAttempts = useRef(new Set());

  // Load kit components for kit products
  const loadKitComponents = useCallback(async () => {
    if (!product || !isKitProduct(product)) return;

    // Check if we've already attempted this product globally
    const productKey = `${product.id}`;
    if (kitStatusAttempts.current.has(productKey)) return;

    // Prevent loading if already loading or already loaded
    if (loadingKitComponents || kitComponents.length > 0) return;

    kitStatusAttempts.current.add(productKey); // Mark as attempted globally
    setLoadingKitComponents(true);
    try {
      const kitStatus = await getKitStatus(token, product.id);
      setKitComponents(kitStatus.kit_components || []);
    } catch (err) {
      logError('OrderItemComponent', 'Error loading kit components', err);
      // Set empty array to prevent retry attempts
      setKitComponents([]);
    } finally {
      setLoadingKitComponents(false);
    }
  }, [token, product, loadingKitComponents, kitComponents.length]);

  // Load kit components when component mounts or product changes
  useEffect(() => {
    if (!isKitProduct(product)) return;

    // Reset the processed flag when product changes
    if (product?.id) {
      hasProcessedExistingDataRef.current = false;
    }

    // Check if we have existing data in Formik
    const existingData = formik.values.items[orderItemIndex]?.kitComponentLots;

    if (
      existingData &&
      existingData.length > 0 &&
      !hasProcessedExistingDataRef.current
    ) {
      // We'll get the real names from the API
      loadKitComponents();
      hasProcessedExistingDataRef.current = true; // Mark as processed
    } else if (!hasProcessedExistingDataRef.current) {
      loadKitComponents();
    }
  }, [product?.id, orderItemIndex, loadKitComponents]); // eslint-disable-line react-hooks/exhaustive-deps

  // Additional effect to handle Formik values changes (for when editing existing orders)
  useEffect(() => {
    if (isKitProduct(product)) {
      const existingData =
        formik.values.items[orderItemIndex]?.kitComponentLots;

      if (
        existingData &&
        existingData.length > 0 &&
        kitComponents.length === 0
      ) {
        // Load real component data from API instead of placeholder data
        loadKitComponents();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, orderItemIndex, kitComponents.length]); // Removed formik.values.items and product to prevent infinite loop

  // Separate effect to handle API loading when no existing data
  useEffect(() => {
    if (
      isKitProduct(product) &&
      !hasProcessedExistingDataRef.current &&
      kitComponents.length === 0 &&
      !loadingKitComponents
    ) {
      loadKitComponents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, orderItemIndex, loadingKitComponents, loadKitComponents]); // Removed kitComponents.length to prevent infinite loop

  // Initialize kit component lots when kit components are loaded (only once)
  useEffect(() => {
    if (kitComponents.length > 0 && isKitProduct(product)) {
      const currentItem = formik.values.items[orderItemIndex];
      const existingKitComponentLots = currentItem?.kitComponentLots || [];

      // Only initialize once when lots are empty to avoid overwriting manual edits.
      if (existingKitComponentLots.length === 0) {
        const initializedKitComponentLots = kitComponents.map(kitComponent => ({
          componentId: kitComponent.component.id,
          lots: [createDefaultKitComponentLotItem(kitComponent)],
        }));

        formik.setFieldValue(
          `items[${orderItemIndex}].kitComponentLots`,
          initializedKitComponentLots
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitComponents.length, product?.id, orderItemIndex]); // Only depend on length and IDs, not the full objects

  // Ensure kit component lots are always present when rendering
  useEffect(() => {
    if (kitComponents.length > 0 && isKitProduct(product)) {
      const currentItem = formik.values.items[orderItemIndex];
      const existingKitComponentLots = currentItem?.kitComponentLots || [];

      // If kit component lots are missing or empty, reinitialize them
      if (existingKitComponentLots.length === 0) {
        const initializedKitComponentLots = kitComponents.map(kitComponent => ({
          componentId: kitComponent.component.id,
          lots: [createDefaultKitComponentLotItem(kitComponent)],
        }));

        formik.setFieldValue(
          `items[${orderItemIndex}].kitComponentLots`,
          initializedKitComponentLots
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitComponents.length, product?.id, orderItemIndex]);

  // Helper to create a default kit component lot item
  const createDefaultKitComponentLotItem = kitComponent => ({
    expiry: '',
    batch: '',
    storageLocation: kitComponent?.component?.location || '',
  });

  // Handler for "Did not arrive" reason
  const handleDidNotArrive = (updatedItems, orderItemIndex) => {
    const newLotItemsArray = [];

    // Batch lotItems, quantity, and selectedReason update in one shot
    handleOrderItemInstantChange(null, {
      lotItems: newLotItemsArray,
      orderItemQuantity: 0,
      status: 'Did not arrive',
    });

    return {
      ...updatedItems[orderItemIndex],
      orderItemQuantity: 0,
      lotItems: newLotItemsArray,
      status: 'Did not arrive',
    };
  };

  // Handler for "Different quantity"
  const handleDifferentQuantity = (
    updatedItems,
    orderItemIndex,
    quoteItemQuantity,
    orderObjItemQuantity
  ) => {
    let lotItems =
      updatedItems[orderItemIndex].lotItems?.length > 0
        ? updatedItems[orderItemIndex].lotItems
        : orderObjItemLotItems || [];

    if (lotItems.length === 0) {
      lotItems = [createDefaultLotItem()];
    }

    // Batch relevant fields
    handleOrderItemInstantChange(null, {
      lotItems,
      orderItemQuantity: orderObjItemQuantity || quoteItemQuantity,
      status: 'Different quantity',
    });

    return {
      ...updatedItems[orderItemIndex],
      orderItemQuantity: orderObjItemQuantity || quoteItemQuantity,
      lotItems,
      status: 'Different quantity',
    };
  };

  // Handler for "Standard reason"
  const handleStandardReason = (
    updatedItems,
    orderItemIndex,
    quoteItemQuantity,
    reason
  ) => {
    let lotItems =
      updatedItems[orderItemIndex].lotItems?.length > 0
        ? updatedItems[orderItemIndex].lotItems
        : orderObjItemLotItems || [];

    if (lotItems.length === 0) {
      lotItems = [createDefaultLotItem()];
    }

    // Batch update, including selectedReason
    handleOrderItemInstantChange(null, {
      lotItems,
      orderItemQuantity: quoteItemQuantity,
      status: reason,
    });

    return {
      ...updatedItems[orderItemIndex],
      orderItemQuantity: quoteItemQuantity,
      lotItems,
      selectedReason: reason,
    };
  };

  // Main reason change handler function
  const handleReasonChange = value => {
    // Copy current form values for the order items
    const updatedItems = [...formik.values.items];
    const quoteItemQuantity = getQuoteItemQuantity(quoteItem, item);

    // Update status in component state
    handleOrderItemInstantChange('status', value);

    // Initialize base updates
    updatedItems[orderItemIndex] = {
      ...updatedItems[orderItemIndex],
      selectedReason: value,
    };

    // Handle specific reason cases
    if (value === 'Did not arrive') {
      updatedItems[orderItemIndex] = handleDidNotArrive(
        updatedItems,
        orderItemIndex
      );
    } else if (value === 'Different quantity') {
      updatedItems[orderItemIndex] = handleDifferentQuantity(
        updatedItems,
        orderItemIndex,
        quoteItemQuantity,
        orderObjItemQuantity
      );
    } else {
      updatedItems[orderItemIndex] = handleStandardReason(
        updatedItems,
        orderItemIndex,
        quoteItemQuantity,
        value
      );
    }

    // Update Formik state
    formik.setValues({
      ...formik.values,
      items: updatedItems,
    });
  };

  // Helper function to update both component and Formik state for an item
  const updateItemState = (updatedItems, orderItemIndex, newState) => {
    // Call on the updateItem function from the parent component
    handleOrderItemInstantChange(null, newState); // null means multi-field batch

    // Return updated formik item
    return {
      ...updatedItems[orderItemIndex],
      ...newState,
    };
  };

  // Helper function to handle lot items
  const getLotItems = existingLotItems => {
    // Prefer currently edited lot values before falling back to server snapshots.
    if (existingLotItems?.length) {
      return existingLotItems;
    }

    if (orderObjItemLotItems?.length) {
      if (process.env.NODE_ENV === 'development') {
        logInfo('OrderItemComponent', 'Falling back to server lot snapshot', {
          orderItemIndex,
          orderObjLotsCount: orderObjItemLotItems.length,
        });
      }
      return orderObjItemLotItems;
    }

    if (!existingLotItems?.length) {
      return [
        {
          expiry: '',
          batch: '',
          lotItemQuantity: 0,
          stockItemIds: [],
        },
      ];
    }

    return existingLotItems;
  };

  // Handler for unchecked state
  const handleUncheckedState = orderItemIndex => {
    const lotItems = getLotItems(item.lotItems);

    return updateItemState(item, orderItemIndex, {
      status: '',
      itemFulfilled: false,
      selectedReason: orderItemStatus || '',

      lotItems,
    });
  };

  // Handler for checked state
  const handleCheckedState = (orderItemIndex, quoteItemQuantity) => {
    const lotItems = getLotItems(item.lotItems);

    const newState = {
      status: 'OK',
      orderItemQuantity: quoteItemQuantity,
      selectedReason: '',
      itemFulfilled: true,
      lotItems,
    };

    if (formik.values.items[orderItemIndex]?.otherReasonDetail) {
      newState.issueDetail = '';
    }

    return updateItemState(item, orderItemIndex, newState);
  };

  // Main checkbox handler function
  const handleCheckbox = () => {
    const quoteItemQuantity = quoteItem?.quantity || item.quoteItem.quantity;

    // Call the relevant function based on the current fulfillment status
    const updatedItem = formik.values.items[orderItemIndex].itemFulfilled
      ? handleUncheckedState(orderItemIndex)
      : handleCheckedState(orderItemIndex, quoteItemQuantity);

    const updatedItems = [...formik.values.items];
    updatedItems[orderItemIndex] = updatedItem; // <- only replace the item at this index

    formik.setValues({
      ...formik.values,
      items: updatedItems,
    });
  };

  // Handler for order item inputs that has delay in updating their changes.
  // It clears the previously set timeout and sets a new one.
  const handleOrderItemDelayedChange = (name, value) => {
    if (typingTimeout) clearTimeout(typingTimeout);

    const newTimeout = setTimeout(() => {
      onItemChange(orderItemIndex, name, value);
    }, 300);

    setTypingTimeout(newTimeout);
  };

  // Handler for order item inputs that should update their changes instantly.
  const handleOrderItemInstantChange = (name, value) => {
    onItemChange(orderItemIndex, name, value);
  };

  // Field names for Formik form fields
  const orderItemQuantityFieldName = `items[${orderItemIndex}].orderItemQuantity`;
  const itemFulfilledStatus = `items[${orderItemIndex}].itemFulfilled`;
  const selectedReasonFieldName = `items[${orderItemIndex}].selectedReason`;
  const otherReasonDetailFieldName = `items[${orderItemIndex}].otherReasonDetail`;

  // Showing a loading spinner if item is undefined or null
  if (!item) {
    return (
      <Spinner
        size="lg"
        as="span"
        animation="border"
        role="status"
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      className={`order-item-panel order-glass-panel${
        hasItemQuantityMismatch(item)
          ? ' order-glass-panel--quantity-mismatch'
          : ''
      }`}
    >
      {/* Conditional rendering, we check if the 'items' array exists and if the item at current index exists */}
      {formik?.values?.items && formik?.values?.items[orderItemIndex] && (
        <>
          <h3 className="order-item-panel__title">
            {/* Displaying the product name and catalogue number */}
            <div className="d-flex align-items-center gap-2">
              <span className="fs-4">🧪</span>
              <OrderProductLabel
                product={product}
                productId={product?.id}
                triggerClassName="order-product-modal-trigger"
                handleProductUpdateFromSupplierTable={() => {}}
              />
            </div>
          </h3>

          {/* Quantity input field */}
          <Form.Group
            controlId={`orderItemQuantity${orderItemIndex}`}
            className="field-margin"
          >
            <Form.Label>Total Received Quantity</Form.Label>
            <NumberInput
              name={orderItemQuantityFieldName}
              integerOnly
              value={(() => {
                return allReceivedOK
                  ? String(item.orderItemQuantity ?? '')
                  : (formik.values?.items[orderItemIndex]?.orderItemQuantity ?? '');
              })()}
              onChange={event => {
                formik.handleChange(event);
                handleOrderItemDelayedChange(
                  'orderItemQuantity',
                  event.target.value
                );
              }}
              onBlur={formik.handleBlur}
              isValid={
                !formik.errors.items?.[orderItemIndex]?.orderItemQuantity &&
                formik.values.items?.[orderItemIndex]?.orderItemQuantity
              }
              isInvalid={
                !!formik.errors.items?.[orderItemIndex]?.orderItemQuantity &&
                formik.touched.items?.[orderItemIndex]?.orderItemQuantity
              }
              disabled={
                formik.values.items[orderItemIndex].selectedReason !==
                'Different quantity'
              }
            />

            {/* Check box for whether item has arrived and is in good condition */}
            <Form.Group
              controlId={`formItemFulfilled${orderItemIndex}`}
              className="field-margin mt-2"
            >
              <Form.Check
                name={itemFulfilledStatus}
                type="checkbox"
                label="Items arrived in full & In good condition"
                checked={allReceivedOK || formik.values?.items[orderItemIndex]?.itemFulfilled}
                onChange={handleCheckbox}
                onBlur={formik.handleBlur}
              />
            </Form.Group>

            {/* Depending on whether item has been fulfilled, we show a dropdown of predefined reasons */}
            {!formik.values?.items[orderItemIndex]?.itemFulfilled && (
              <>
                <Form.Group
                  controlId={`formSelectedReason${orderItemIndex}`}
                  className="field-margin"
                >
                  {/* Providing predefined reasons */}
                  {/* User can also enter their own reasons */}
                  <Form.Select
                    name={selectedReasonFieldName}
                    onChange={event => {
                      const { value } = event.target;
                      handleReasonChange(value);
                    }}
                    value={
                      formik.values?.items[orderItemIndex]?.selectedReason || ''
                    }
                    onBlur={formik.handleBlur}
                  >
                    <option value="" disabled>
                      --Select a Reason--
                    </option>
                    <option value="Did not arrive">Did not arrive</option>
                    <option value="Different quantity">
                      Different quantity
                    </option>
                    <option value="Wrong Item">Wrong Item</option>
                    <option value="Expired or near expiry">
                      Expired or near expiry
                    </option>
                    <option value="Bad condition">Bad condition</option>
                    <option value="Other">Other...</option>
                  </Form.Select>
                </Form.Group>

                {/* If 'Other' reason is selected, we provide an additional textarea for the user to specify the issue */}
                {formik.values?.items[orderItemIndex]?.selectedReason ===
                  'Other' && (
                  <Form.Group
                    controlId={`formOtherReasonDetails${orderItemIndex}`}
                  >
                    {/* Props for FormControl */}
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name={otherReasonDetailFieldName}
                      placeholder="Specify the issue"
                      value={
                        formik.values?.items[orderItemIndex]
                          ?.otherReasonDetail || ''
                      }
                      onChange={event => {
                        const { value } = event.target;
                        formik.handleChange(event);
                        handleOrderItemDelayedChange('issueDetail', value);
                      }}
                      onBlur={formik.handleBlur}
                    />
                  </Form.Group>
                )}
              </>
            )}

            {/* Adding an invalid feedback component for quantity input field */}
            <Form.Control.Feedback type="invalid">
              {formik?.errors?.items?.[orderItemIndex]?.orderItemQuantity}
            </Form.Control.Feedback>
          </Form.Group>
          {/* Render the stock item component via mapping over the lot items array */}
          {/* Only render lot items if item status is not "Did not arrive" */}
          {item.status !== 'Did not arrive' &&
            (item.lotItems ||
              (isKitProduct(product)
                ? [{ expiry: '', batch: '', lotItemQuantity: 0 }]
                : [])) &&
            (
              item.lotItems ||
              (isKitProduct(product)
                ? [{ expiry: '', batch: '', lotItemQuantity: 0 }]
                : [])
            ).map((lotItem, lotIndex) => (
              <OrderItemLotComponent
                key={lotIndex}
                orderItemIndex={orderItemIndex} // Order item index for Formik state management
                lotIndex={lotIndex} // stock item index for Formik state management
                onLotChange={onItemChange} // The parent level function in charge of updating the items state
                showRemoveButton={
                  (
                    item.lotItems ||
                    (isKitProduct(product)
                      ? [{ expiry: '', batch: '', lotItemQuantity: 0 }]
                      : [])
                  ).length === 1
                }
                handleLotItemDelete={handleLotItemDelete}
                formik={formik} // Formik props
                allReceivedOK={allReceivedOK}
                lotItem={lotItem}
                storageNodes={storageNodes}
                storagePlaceOptions={storagePlaceOptions}
              />
            ))}

          {hasItemQuantityMismatch(item) && <QuantityErrorMessage />}
          {/* Only show add lot item button if status is not "Did not arrive" */}
          {item.status !== 'Did not arrive' && (
            <Button
              className="field-margin"
              onClick={e => {
                addLotItem(e, orderItemIndex);
              }}
              variant="outline-success"
            >
              <Plus size={30} />
            </Button>
          )}

          {/* Kit Components Section */}
          {isKitProduct(product) && item.status !== 'Did not arrive' && (
            <div className="mt-4">
              <Accordion>
                <Accordion.Item eventKey="kit-components">
                  <Accordion.Header>
                    <strong>Kit Components</strong>
                    <Badge bg="info" className="ms-2">
                      {kitComponents.length} components
                    </Badge>
                  </Accordion.Header>
                  <Accordion.Body>
                    {loadingKitComponents ? (
                      <div className="d-flex justify-content-center p-3">
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2">Loading kit components...</span>
                      </div>
                    ) : kitComponents.length > 0 ? (
                      <div>
                        {kitComponents.map(
                          (kitComponent, kitComponentIndex) => (
                            <div
                              key={kitComponent.component.id}
                              className="mb-4"
                            >
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0 d-flex align-items-center">
                                  <span>{kitComponentIndex + 1}. </span>
                                  <span className="ms-1 bottle-emoji">🧪</span>
                                  <OrderProductLabel
                                    product={kitComponent.component}
                                    productId={kitComponent.component.id}
                                    className="kit-component"
                                    triggerClassName="order-product-modal-trigger order-product-modal-trigger--kit"
                                    handleProductUpdateFromSupplierTable={() => {}}
                                  />
                                </h6>
                                <div className="d-flex gap-1">
                                  <Badge bg="primary">
                                    Qty: {kitComponent.quantity_per_kit}
                                  </Badge>
                                  {kitComponent.is_required ? (
                                    <Badge bg="warning">Required</Badge>
                                  ) : (
                                    <Badge bg="secondary">Optional</Badge>
                                  )}
                                </div>
                              </div>

                              {/* Kit Component Lots */}
                              {(() => {
                                // Get lots for this specific component from Formik state
                                const currentItem =
                                  formik.values.items[orderItemIndex];
                                const kitComponentLots =
                                  currentItem?.kitComponentLots || [];
                                const componentLots =
                                  kitComponentLots[kitComponentIndex]?.lots ||
                                  [];

                                // If no lots exist for this component, show empty state
                                if (componentLots.length === 0) {
                                  return (
                                    <div className="text-center text-muted p-3">
                                      <small>
                                        No lots configured for this component
                                      </small>
                                    </div>
                                  );
                                }

                                // Render existing lots
                                return componentLots.map(
                                  (lotItem, lotIndex) => (
                                    <OrderItemKitComponentLotComponent
                                      key={lotIndex}
                                      orderItemIndex={orderItemIndex}
                                      kitComponentIndex={kitComponentIndex}
                                      lotIndex={lotIndex}
                                      kitComponent={kitComponent}
                                      formik={formik}
                                    />
                                  )
                                );
                              })()}
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="text-center text-muted">
                        No kit components found
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrderItemComponent;
